import React, { useEffect, useRef, useState } from "react";
import ContactStatePill from "@/components/contacts/ContactStatePill";
import { getContactDisplayName } from "@/util/string";
import { memberName } from "@/util/member";
import { useGetTasksQuery, useGetTaskQuery, useUpdateTaskStatusMutation, useDeleteTaskMutation, useGetMembersQuery } from "@/store/rest_api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Search, Pencil, Trash2, User, Calendar, X, Link2, MoreVertical, Upload } from "lucide-react";
import ImportWizard from "@/components/imports/ImportWizard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import type Task from "@/model/interface/Task";
import type { TaskStatus, TaskPriority } from "@/model/interface/Task";
import TaskFormDialog from "@/components/pages/tasks/TaskFormDialog";
import ContactActivitySheet from "@/components/contacts/ContactActivitySheet";

const STATUS_CONFIG: Record<TaskStatus, { label: string; color: string }> = {
  TODO: { label: "To Do", color: "bg-slate-500" },
  IN_PROGRESS: { label: "In Progress", color: "bg-blue-500" },
  DONE: { label: "Done", color: "bg-green-500" },
};

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string }> = {
  LOW: { label: "Low", color: "text-slate-500" },
  MEDIUM: { label: "Medium", color: "text-blue-500" },
  HIGH: { label: "High", color: "text-orange-500" },
  URGENT: { label: "Urgent", color: "text-red-500" },
};

export default function TasksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkTaskId = searchParams.get("task");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [priorityFilter, setPriorityFilter] = useState<string>("");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("");
  const [page, setPage] = useState(0);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [activityContactId, setActivityContactId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const autoOpenedRef = useRef(false);

  const { data: members = [] } = useGetMembersQuery({ status: "ACTIVE" });
  const { data, isLoading } = useGetTasksQuery({
    page,
    size: 20,
    q: search || undefined,
    status: (statusFilter || undefined) as TaskStatus | undefined,
    priority: (priorityFilter || undefined) as TaskPriority | undefined,
    assignedMembershipId: assigneeFilter ? Number(assigneeFilter) : undefined,
  });
  const [updateTaskStatus] = useUpdateTaskStatusMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const { data: deepLinkTask } = useGetTaskQuery(deepLinkTaskId ?? "", { skip: !deepLinkTaskId });

  const tasks = data?.items ?? [];
  const filtersActive = !!(statusFilter || priorityFilter || assigneeFilter);

  const handleToggleComplete = async (task: Task) => {
    const newStatus = task.status === "DONE" ? "TODO" : "DONE";
    try {
      await updateTaskStatus({ id: task.id, status: newStatus }).unwrap();
    } catch {
      toast.error("Failed to update task");
    }
  };

  const handleDelete = async (taskId: string) => {
    try {
      await deleteTask(taskId).unwrap();
      toast.success("Task deleted");
    } catch {
      toast.error("Failed to delete task");
    }
  };

  const openTask = (task: Task) => {
    setEditingTask(task);
    setShowFormDialog(true);
    searchParams.set("task", task.id);
    setSearchParams(searchParams, { replace: false });
  };

  const copyTaskLink = async (taskId: string) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/tasks?task=${taskId}`);
      toast.success("Task link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const handleCreate = () => {
    setEditingTask(null);
    setShowFormDialog(true);
  };

  const clearFilters = () => {
    setStatusFilter("");
    setPriorityFilter("");
    setAssigneeFilter("");
  };

  const isOverdue = (task: Task) => {
    if (!task.dueDate || task.status === "DONE") return false;
    return new Date(task.dueDate) < new Date(new Date().toDateString());
  };

  const handleDialogOpenChange = (open: boolean) => {
    setShowFormDialog(open);
    if (!open) {
      setEditingTask(null);
      if (deepLinkTaskId) {
        searchParams.delete("task");
        setSearchParams(searchParams, { replace: true });
      }
    }
  };

  useEffect(() => {
    if (deepLinkTask && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setEditingTask(deepLinkTask);
      setShowFormDialog(true);
    }
  }, [deepLinkTask]);

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tasks</h1>
          <p className="text-muted-foreground">Manage tasks for your team</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4 mr-1" />
            Import
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-1" />
            New Task
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tasks..."
            value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(0); }}
            className="pl-9"
          />
        </div>

        <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value === "ALL" ? "" : value); setPage(0); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            <SelectItem value="TODO">To Do</SelectItem>
            <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
            <SelectItem value="DONE">Done</SelectItem>
          </SelectContent>
        </Select>

        <Select value={priorityFilter} onValueChange={(value) => { setPriorityFilter(value === "ALL" ? "" : value); setPage(0); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Priorities</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="HIGH">High</SelectItem>
            <SelectItem value="URGENT">Urgent</SelectItem>
          </SelectContent>
        </Select>

        <Select value={assigneeFilter} onValueChange={(value) => { setAssigneeFilter(value === "ALL" ? "" : value); setPage(0); }}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Members</SelectItem>
            {members.map((member) => (
              <SelectItem key={member.id} value={String(member.id)}>
                {memberName(member)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-16 w-full" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <p className="text-sm">No tasks found</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-1" />
            Create a task
          </Button>
        </div>
      ) : (
        <div className="space-y-1">
          {tasks.map((task) => {
            const statusConfig = STATUS_CONFIG[task.status];
            const priorityConfig = PRIORITY_CONFIG[task.priority];
            const overdue = isOverdue(task);

            return (
              <div
                key={task.id}
                role="button"
                tabIndex={0}
                onClick={() => openTask(task)}
                className={`flex cursor-pointer items-center gap-3 px-4 py-3 rounded-lg border hover:bg-muted/50 transition-colors ${task.status === "DONE" ? "opacity-60" : ""}`}
              >
                <Checkbox
                  checked={task.status === "DONE"}
                  onClick={(event) => event.stopPropagation()}
                  onCheckedChange={() => handleToggleComplete(task)}
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${task.status === "DONE" ? "line-through text-muted-foreground" : ""}`}>
                      {task.title}
                    </span>
                    <Badge variant="outline" className="text-xs shrink-0">
                      <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1 ${statusConfig.color}`} />
                      {statusConfig.label}
                    </Badge>
                    <span className={`text-xs font-medium ${priorityConfig.color}`}>
                      {priorityConfig.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                    {task.dueDate && (
                      <span className={`flex items-center gap-1 ${overdue ? "text-red-500 font-medium" : ""}`}>
                        <Calendar className="h-3 w-3" />
                        {new Date(task.dueDate).toLocaleDateString()}
                        {overdue && " (overdue)"}
                      </span>
                    )}
                    {task.assignedMembership && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {memberName(task.assignedMembership)}
                      </span>
                    )}
                    {task.contact && (
                      <span className="inline-flex items-center gap-1.5">
                        <button
                          className="hover:underline"
                          onClick={(event) => { event.stopPropagation(); setActivityContactId(task.contact!.id); }}
                        >
                          {getContactDisplayName(task.contact) ?? "Contact"}
                        </button>
                        <ContactStatePill contact={task.contact} />
                      </span>
                    )}
                    {task.deal && (
                      <button
                        className="hover:underline"
                        onClick={(event) => { event.stopPropagation(); navigate(`/deals/${task.deal!.id}`); }}
                      >
                        {task.deal.propertyAddress || "Deal"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    title="Edit"
                    onClick={(event) => { event.stopPropagation(); openTask(task); }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    title="Copy link"
                    onClick={(event) => { event.stopPropagation(); copyTaskLink(task.id); }}
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(event) => event.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Options">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={(event) => { event.stopPropagation(); handleDelete(task.id); }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete task
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {data.totalElements} task{data.totalElements !== 1 ? "s" : ""}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={data.last} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}

      <TaskFormDialog
        open={showFormDialog}
        onOpenChange={handleDialogOpenChange}
        task={editingTask}
      />

      <ContactActivitySheet
        contactId={activityContactId}
        onOpenChange={(open) => { if (!open) { setActivityContactId(null); } }}
      />

      <ImportWizard open={importOpen} onClose={() => setImportOpen(false)} entityType="TASK" />
    </div>
  );
}
